import {Nullable} from "../../definitions/types";

export class EmployeeNavigationModel {
    private readonly _id: number;
    private readonly _primaryId: Nullable<number>;
    private readonly _sku: Nullable<string>;
    private readonly _primarySku: Nullable<string>;
    private readonly _quantity: number;
    private readonly _targetKeyPhrase: Nullable<string>;
    private readonly _reportingClassName: Nullable<string>;
    private readonly _environmentalScore: Nullable<number>;
    private readonly _rank: number;
    private readonly _launchDate: Date;
    private readonly _status: string;
    private readonly _quantityOnHand: number;
    private readonly _inTransitDays: Nullable<number>;
    private readonly _inTransitQuantity: Nullable<number>;
    private readonly _isInTransit: boolean;
    private readonly _isAutoOffline: boolean;
    private readonly _isBuiltToOrder: boolean;
    private readonly _isHardMinimumOrder: boolean;
    private readonly _isInStock: boolean;
    private readonly _isNewProduct: boolean;
    private readonly _isOutOfStockNoBackOrder: boolean;
    private readonly _leadIn: number;
    private readonly _leadOut: number;

    constructor(response: {
        product: Nullable<{
            id: number,
            primaryId?: number,
            sku: string,
            primarySku?: string,
            quantity: number,
            targetKeyPhrase?: string,
            reportingClassName?: string,
            environmentalScore?: number,
            rank: number,
            launchDate: Date,
            stockInformation: Nullable<{
                id: number,
                status?: string,
                quantityOnHand: number,
                inTransitDays?: number,
                inTransitQuantity?: number
                isInTransit: boolean,
                isAutoOffline: boolean,
                isBuiltToOrder: boolean,
                isHardMinimumOrder: boolean,
                isInStock: boolean,
                isNewProduct: boolean,
                isOutOfStockNoBackOrder: boolean,
                leadIn: number,
                leadOut: number
            }>
        }>
    }) {
        this._id = response.product?.id ?? 0;
        this._primaryId = response.product?.primaryId ?? null;
        this._sku = response.product?.sku ?? null;
        this._primarySku = response.product?.primarySku ?? null;
        this._quantity = response.product?.quantity ?? 0;
        this._targetKeyPhrase = response.product?.targetKeyPhrase ?? null;
        this._reportingClassName = response.product?.reportingClassName ?? null;
        this._environmentalScore = response.product?.environmentalScore ?? null;
        this._rank = response.product?.rank ?? Number.MAX_VALUE;
        this._launchDate = response.product?.launchDate ?? new Date();
        this._status = response.product?.stockInformation?.status ?? 'unknown';
        this._quantityOnHand = response.product?.stockInformation?.quantityOnHand ?? 0;
        this._inTransitDays = response.product?.stockInformation?.inTransitDays ?? null;
        this._inTransitQuantity = response.product?.stockInformation?.inTransitQuantity ?? null;
        this._isInTransit = response.product?.stockInformation?.isInTransit ?? false;
        this._isAutoOffline = response.product?.stockInformation?.isAutoOffline ?? false;
        this._isBuiltToOrder = response.product?.stockInformation?.isBuiltToOrder ?? false;
        this._isHardMinimumOrder = response.product?.stockInformation?.isHardMinimumOrder ?? false;
        this._isInStock = response.product?.stockInformation?.isInStock ?? false;
        this._isNewProduct = response.product?.stockInformation?.isNewProduct ?? false;
        this._isOutOfStockNoBackOrder = response.product?.stockInformation?.isOutOfStockNoBackOrder ?? false;
        this._leadIn = response.product?.stockInformation?.leadIn ?? 0;
        this._leadOut = response.product?.stockInformation?.leadOut ?? 0;
    }

    /**
     * @property id
     * @description The product id
     * @returns {number}
     */
    public get id(): number {
        return this._id;
    }

    /**
     * @property primaryId
     * @description The primary product id
     * @returns {Nullable<number>}
     */
    public get primaryId(): Nullable<number> {
        return this._primaryId;
    }

    /**
     * @property sku
     * @description The product sku
     * @returns {Nullable<string>}
     */
    public get sku(): Nullable<string> {
        return this._sku;
    }

    /**
     * @property primarySku
     * @description The primary product sku
     * @returns {Nullable<string>}
     */
    public get primarySku(): Nullable<string> {
        return this._primarySku;
    }

    /**
     * @property quantity
     * @description The product quantity
     * @returns {number}
     */
    public get quantity(): number {
        return this._quantity;
    }

    /**
     * @property targetKeyPhrase
     * @description The product target key phrase
     * @returns {Nullable<string>}
     */
    public get targetKeyPhrase(): Nullable<string> {
        return this._targetKeyPhrase;
    }

    /**
     * @property reportingClassName
     * @description The product reporting class name
     * @returns {Nullable<string>}
     */
    public get reportingClassName(): Nullable<string> {
        return this._reportingClassName;
    }

    /**
     * @property environmentalScore
     * @description The product environmental score
     * @returns {Nullable<number>}
     */
    public get environmentalScore(): Nullable<number> {
        return this._environmentalScore;
    }

    /**
     * @property rank
     * @description The product rank
     * @returns {number}
     */
    public get rank(): number {
        return this._rank;
    }

    /**
     * @property launchDate
     * @description The product launch date
     * @returns {string}
     */
    public get launchDate(): string {
        return new Date(this._launchDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        })
    }

    /**
     * @property status
     * @description The product status
     * @returns {string}
     */
    public get status(): string {
        return this._status;
    }

    /**
     * @property quantityOnHand
     * @description The product quantity on hand
     * @returns {number}
     */
    public get quantityOnHand(): number {
        return this._quantityOnHand;
    }

    /**
     * @property inTransitDays
     * @description The product in transit days
     * @returns {Nullable<number>}
     */
    public get inTransitDays(): Nullable<number> {
        return this._inTransitDays;
    }

    /**
     * @property inTransitQuantity
     * @description The product in transit quantity
     * @returns {Nullable<number>}
     */
    public get inTransitQuantity(): Nullable<number> {
        return this._inTransitQuantity;
    }

    /**
     * @property isInTransit
     * @description The product is in transit
     * @returns {boolean}
     */
    public get isInTransit(): boolean {
        return this._isInTransit;
    }

    /**
     * @property isAutoOffline
     * @description The product is auto offline
     * @returns {boolean}
     */
    public get isAutoOffline(): boolean {
        return this._isAutoOffline;
    }

    /**
     * @property isBuiltToOrder
     * @description The product is built to order
     * @returns {boolean}
     */
    public get isBuiltToOrder(): boolean {
        return this._isBuiltToOrder;
    }

    /**
     * @property isHardMinimumOrder
     * @description The product is hard minimum order
     * @returns {boolean}
     */
    public get isHardMinimumOrder(): boolean {
        return this._isHardMinimumOrder;
    }

    /**
     * @property isInStock
     * @description The product is in stock
     * @returns {boolean}
     */
    public get isInStock(): boolean {
        return this._isInStock;
    }

    /**
     * @property isNewProduct
     * @description The product is new product
     * @returns {boolean}
     */
    public get isNewProduct(): boolean {
        return this._isNewProduct;
    }

    /**
     * @property isOutOfStockNoBackOrder
     * @description The product is out of stock no back order
     * @returns {boolean}
     */
    public get isOutOfStockNoBackOrder(): boolean {
        return this._isOutOfStockNoBackOrder;
    }

    /**
     * @property leadIn
     * @description The product lead in
     * @returns {number}
     */
    public get leadIn(): number {
        return this._leadIn;
    }

    /**
     * @property leadOut
     * @description The product lead out
     * @returns {number}
     */
    public get leadOut(): number {
        return this._leadOut;
    }

    /**
     * @property frequentlyAskedQuestionHref
     * @description The product frequently asked question href
     * @returns {string}
     */
    public get frequentlyAskedQuestionHref(): string {
        return `//productfaq.d2go.net/productfaq.aspx?sku=${this.sku}`.toLowerCase();
    }
}